import React, { useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import useGlobalState from '../../GlobalStateContext';
import ActionButton from '../../elements/ActionButton';
import ProtectedLink from '../../elements/ProtectedLink';
import Search from '../../elements/Search';
import BooleanIcon from '../../elements/booleanIcon';
import ColoredStatus from '../../elements/common/ColoredStatus';
import Paginator from '../../elements/paginator';
import { PERMISSION } from '../../helpers/Permissions';

import ManualWithdrawModal from './ManualWithdrawModal';

import CRMApi from 'Api/CRM/CRMApi';
import { WithdrawalStatus } from 'Api/CRM/Responses/Enums/WithdrawalStatus';
import GetWithdrawalResponse from 'Api/CRM/Responses/WithdrawalResponse';
import { SelectOptions } from 'components/common/SelectInput';
import DownloadButton from 'crm/elements/DownloadButton';
import Card from 'crm/elements/common/Card';
import FilterByColumn, { Filter } from 'crm/elements/common/FilterByColumn';
import SortButton from 'crm/elements/common/SortButton';
import { TableCell as Cell } from 'crm/elements/common/TableDetailsRow';
import TableHead from 'crm/elements/common/TableHead';
import TableLoader from 'crm/elements/common/TableLoader';
import { createOptionsFromEnum } from 'crm/helpers/formUtils';
import useAutoPaginator, { FetchPaginatorParams } from 'crm/hooks/useAutoPaginator';
import useFetchState from 'crm/hooks/useFetchState';
import { formatDateStringWithTimezone } from 'utils/formatters';

const withdrawalStatuses = createOptionsFromEnum(WithdrawalStatus);

type Props = {
    addManualButton?: boolean;
} & ({ source?: 'withdrawals'; relatedId?: never } | { source: 'user'; relatedId: number });

function WithdrawalsList({ source = 'withdrawals', relatedId, addManualButton = false }: Readonly<Props>) {
    const isMainSource = source === 'withdrawals';
    const url = isMainSource ? 'withdrawal' : `user/${relatedId}/withdrawals`;
    const isUserDetails = useRef(useLocation().pathname.includes('users')).current;
    const { loggedAdmin } = useGlobalState();
    const [queryParams, setQueryParams] = useState<FetchPaginatorParams>();

    const { items, updateItems, isLoading, auto } = useAutoPaginator<GetWithdrawalResponse>(async (page, params) => {
        setQueryParams(params);

        return (await CRMApi.get(url, { params: { page, ...params } })).data;
    });

    const [manualWithdrawEnabled] = useFetchState(false, (setState) =>
        CRMApi.withdrawals.isManualWithdrawEnabled().then(setState)
    );
    const [methodNames] = useFetchState({} as SelectOptions, (setState) =>
        CRMApi.withdrawalMethods.getNames().then(setState)
    );

    const tableHeader = (
        <>
            <span className="mr-3">{isMainSource ? 'All Withdrawals' : 'Withdrawals'}</span>
            {isMainSource && (
                <DownloadButton
                    action="withdrawals-as-csv"
                    title="Export to CSV"
                    permissions={[PERMISSION.DOWNLOAD_WITHDRAWALS]}
                    defaultFilename="withdrawals.csv"
                    params={queryParams}
                />
            )}
            {addManualButton &&
                loggedAdmin.hasPermission(PERMISSION.CREATE_MANUAL_WITHDRAWAL) &&
                manualWithdrawEnabled && <ManualWithdrawModal userId={relatedId} onSuccess={updateItems} />}
        </>
    );

    return (
        <Card title={tableHeader}>
            <Search {...auto.search} />
            <FilterByColumn {...auto.filter}>
                <Filter column="withdrawal_method_id" type="select" label="Withdrawal method" options={methodNames} />
                <Filter column="status" type="select" options={withdrawalStatuses} />
                <Filter column="date_confirmed" type="dateRange" />
                <Filter column="withdrawals.created_at" label="Created at" type="dateRange" />
            </FilterByColumn>
            <TableLoader isLoading={isLoading}>
                <table className="table model-list">
                    <TableHead>
                        <th>Action</th>
                        <SortButton column="token" {...auto.sort} />
                        {!isUserDetails && [
                            <SortButton key="email" column="email" label="E-mail" {...auto.sort} />,
                            <SortButton
                                key="kyc"
                                column="users.is_kyc_confirmed"
                                label="KYC confirmed"
                                {...auto.sort}
                            />,
                            <SortButton key="phone" column="phone" {...auto.sort} />,
                        ]}
                        <SortButton column="user_fee" label="Withdrawal cost" {...auto.sort} />
                        <SortButton column="withdrawal_method_id" label="Withdrawal method" {...auto.sort} />
                        <SortButton column="amount" {...auto.sort} />
                        <SortButton column="status" {...auto.sort} />
                        <SortButton column="date_confirmed" {...auto.sort} />
                        <SortButton column="created_at" order="desc" {...auto.sort} />
                    </TableHead>
                    <WithdrawalsTableBody items={items} isUserDetails={isUserDetails} />
                </table>
                <Paginator {...auto.paginator} />
            </TableLoader>
        </Card>
    );
}

function WithdrawalsTableBody({
    items,
    isUserDetails,
}: Readonly<{ items: GetWithdrawalResponse[]; isUserDetails: boolean }>) {
    return (
        <tbody>
            {items.map((withdrawal) => (
                <tr key={withdrawal.id}>
                    <td>
                        <ActionButton
                            title="open"
                            url={`/withdrawals/${withdrawal.id}`}
                            permissions={[PERMISSION.VIEW_WITHDRAWAL]}
                        />
                    </td>
                    <td>{withdrawal.token}</td>
                    {!isUserDetails && (
                        <>
                            <td>
                                <ProtectedLink
                                    url={`/users/${withdrawal.user_id}`}
                                    value={withdrawal.email}
                                    permissions={[PERMISSION.VIEW_USER]}
                                />
                            </td>
                            <td>
                                <BooleanIcon value={withdrawal.kyc_confirmed} />
                            </td>
                            <Cell>{withdrawal.phone}</Cell>
                        </>
                    )}
                    <td>{withdrawal.user_fee.formatted}</td>
                    <td>{withdrawal.method_name}</td>
                    <td>{withdrawal.amount?.formatted}</td>
                    <td>
                        <ColoredStatus status={withdrawal.status} />
                    </td>
                    <td>{formatDateStringWithTimezone(withdrawal.date_confirmed)}</td>
                    <td>{formatDateStringWithTimezone(withdrawal.created_at)}</td>
                </tr>
            ))}
        </tbody>
    );
}

export default WithdrawalsList;
